/* eslint-disable complexity */
import {
  Box,
  Checkbox,
  DatePicker,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconLabel,
  TextField,
  Tooltip,
} from '@atgfr8/atg-ui-tailwind';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  InformationIcon,
  MinusCircleIcon,
  PlusIcon,
} from '@atgfr8/icons';
import {
  appointmentSchedulingStatusOptions,
  defaultLoadItems,
  StopsMainProps,
} from '@common/stops';
import {
  Button,
  Notification,
  PhoneInput,
  RadioGroup,
  ToastType,
} from '@components';
import AddressAutoComplete, {
  AddressDetails,
} from '@components/Autocomplete/AddressAutoComplete';
import { DebounceAutoComplete } from '@components/Autocomplete/DebounceAutoComplete';
import { IntegerTimeInput } from '@components/IntegerTimeInput';
import { SelectInputCustom } from '@components/Select/SelectInputCustom';
import { TimePicker } from '@components/TimePicker';
import { SchedulingMethodFields } from '@components/FacilityDetails/SchedulingMethodFields';
import { WeeklyOperatingHoursEditor } from '@components/FacilityDetails/WeeklyOperatingHoursEditor';
import {
  createStopFacilityDetails,
  stopFacilityToFacility,
} from '@common/facilityDetails';
import { FeatureFlags } from '@constants/featureFlags';
import { appointmentTypeOptions } from '@views/customer/customerDetail/constants';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { closeModal } from '@store/modals';
import {
  featureFlagGetCustomerFeatures,
  getLocationTimezone,
  orderStore,
  PickUpDropOffItem,
  setToAddressBook,
} from '@store/orders';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import {
  timeIntegerToTimeString,
  timeStringToTimeInteger,
} from '@utils/dateFormat';
import { logError } from '@utils/errorLogging';
import { removeEmojis, sanitizeStateInput } from '@utils/formUtils';
import { cloneBreakdownsForInsert } from '@utils/ltl/loadItemBreakdown';
import { basicUSTimezoneOptions, naTimezoneOptions } from '@utils/timezones';
import { convertDateTimeV2, handleToast, printError } from '@utils/utils';
import { debounce, get } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { DateTime } from 'luxon';
import { createSignal, onMount, Show } from 'solid-js';

import {
  canEditDriverInTimeForEditor,
  canEditDriverOutTimeForEditor,
  positionForStopId,
  validateStopTimeChange,
} from '../StopHelpers';
import { StopAddressBookSync } from './StopAddressBookSync';
import { StopItems } from './StopItems';
import { StopListContainer } from './StopListContainer';

export const StopsMain = (props: StopsMainProps) => {
  const setFields = (...args: Parameters<typeof props.setFields>) =>
    props.setFields(...args);
  const setActiveView = (...args: Parameters<typeof props.setActiveView>) =>
    props.setActiveView(...args);
  const setSmallestLoadItemId = (
    ...args: Parameters<typeof props.setSmallestLoadItemId>
  ) => props.setSmallestLoadItemId(...args);
  const setIsDirty = (...args: Parameters<typeof props.setIsDirty>) =>
    props.setIsDirty(...args);
  const [addingtoAddressBook, setAddingToAddressBook] = createSignal(false);
  const [timezoneOverride, setTimezoneOverride] = createSignal<
    string | undefined
  >(undefined);
  const [schedulingDetailsOpen, setSchedulingDetailsOpen] = createSignal(false);
  const isLtlCleanupEnabled = isFeatureFlagEnabled(
    FeatureFlags.ltlStopModalCleanup,
  );
  const hideLtlFields = () => props.mode === 'LTL' && isLtlCleanupEnabled();
  const is24HourTimesEnabled = isFeatureFlagEnabled(
    FeatureFlags.enable_24_hour_in_and_out_times,
  );
  // Facility "Appointment Type" drives whether a TL stop shows a single
  // appointment date/time or a Begin/End (Driver In/Out) date-time range.
  const isFacilityAppointmentType = () =>
    props.mode === 'TL' &&
    isFeatureFlagEnabled(FeatureFlags.AddressBookFacilityDetails)() &&
    props.data().facilityAppointmentType === 'Appointment';

  onMount(() => {
    if (
      orderStore.order.loads[orderStore.activeTab.index].stops !== undefined
    ) {
      if (
        orderStore.order.loads[orderStore.activeTab.index]?.stops!.length >=
          1 &&
        props.stop === null
      ) {
        setStopsFields('pickUp', false);
        setStopsFields('dropOff', true);
      } else if (!!props.stop && props.stop.timezone === null) {
        getLocationTimezone(props.stop.countryCode ?? 'US', props.stop.zip)
          .then((timezone) => {
            if (timezone !== undefined) {
              // const abbr = getTimeZoneAbbreviation(timezone);
              setStopsFields('timezone', timezone);
            } else {
              setFields('timezone', 'CST');
            }
          })
          .catch(printError);
      }
    }
    props.setFields(
      'loadItems',
      cloneDeep([
        ...(orderStore.order.loads[orderStore.activeTab.index].items ?? []),
      ]),
    );
  });

  const setStopsFields = (field: string, value: unknown) => {
    setIsDirty(true);
    setFields(field, value);
  };

  const [driverInChronologyError, setDriverInChronologyError] =
    createSignal('');
  const [driverOutChronologyError, setDriverOutChronologyError] =
    createSignal('');

  const validateDriverTimeChange = (
    field: 'driverInTime' | 'driverOutTime',
    candidateDate: string,
    candidateTime: string,
    partnerDate: string,
    partnerTime: string,
  ): boolean => {
    const setError =
      field === 'driverInTime'
        ? setDriverInChronologyError
        : setDriverOutChronologyError;
    if (!candidateDate || !candidateTime) {
      setError('');
      return true;
    }
    const candidateIso = convertDateTimeV2(candidateDate, candidateTime);
    const partnerIso =
      partnerDate && partnerTime
        ? convertDateTimeV2(partnerDate, partnerTime)
        : null;
    const position = positionForStopId(props.stop?.id);
    const result = validateStopTimeChange(
      position,
      field,
      candidateIso,
      partnerIso,
    );
    if (!result.ok) {
      setError(result.message);
      return false;
    }
    setError('');
    return true;
  };

  // Normalize a stored driver time (ISO datetime or "HH:mm") to "HH:mm" for display.
  const driverTimeToHHmm = (value: string | null | undefined): string => {
    if (!value) return '';
    if (value.includes('T')) {
      const dt = DateTime.fromISO(value);
      return dt.isValid ? dt.toFormat('HH:mm') : '';
    }
    const match = value.match(/^(\d{1,2}):(\d{2})/);
    return match ? `${match[1].padStart(2, '0')}:${match[2]}` : '';
  };

  const driverTimeToIso = (
    newTime: string | null | undefined,
    currentValue: string,
  ): string => {
    if (!newTime) return '';
    const [datePart] = (
      currentValue.includes('T') ? currentValue : DateTime.now().toISO()
    ).split('T');
    return datePart ? `${datePart}T${newTime}` : newTime;
  };

  const checkTimezone = (country: string | undefined, zip: string) => {
    getLocationTimezone(country ?? 'US', zip)
      .then((timezone) => {
        if (timezone !== undefined) {
          // const abbr = getTimeZoneAbbreviation(timezone);
          setStopsFields('timezone', timezone);
        } else {
          setFields('timezone', 'CST');
        }
      })
      .catch((e) => {
        printError(e);
      });
  };

  const debouncedCheckTimezone = debounce(
    (country: string | undefined, zip: string) => {
      checkTimezone(country, zip);
    },
    500,
  );

  const handleGooglePlacesSelect = (place: AddressDetails) => {
    const { address1 = '', city = '', state = '', zipCode = '' } = place;
    setStopsFields('address1', ''); // Clear first to ensure reactivity
    setStopsFields('city', city);
    setStopsFields('state', state);
    setStopsFields('zip', zipCode);
    setStopsFields('address1', address1);

    if (zipCode) {
      checkTimezone(place.country ?? 'US', zipCode);
    }
  };

  const handleSelectFromAddressBook = () => {
    if (orderStore.order.customerId === null) {
      return;
    }
    try {
      setActiveView('select-from-addresses');
    } catch (error) {
      handleToast(ToastType.Error, (error as Error).message);
    }
  };

  const getTotalWeight = () => {
    return props.data().loadItems.reduce((acc, itm) => {
      if (
        itm.operationType === 'Delete' ||
        (props.data().dropOff && props.data().id != itm.dropOffId) ||
        (!props.data().dropOff && props.data().id !== itm.pickUpId)
      ) {
        acc += 0;
      } else {
        acc += Number(itm.weight);
      }
      return acc;
    }, 0);
  };

  const addNewListItem = () => {
    const loadItems = [
      ...props.data().loadItems,
      {
        ...defaultLoadItems,
        item: props.mode === 'LTL' ? 'Pallets' : 'Items',
        id: props.smallestLoadItemId(),
        dropOffId: props.data().pickUp ? null : props.data().id,
        pickUpId: props.data().pickUp ? props.data().id : null,
      },
    ] as PickUpDropOffItem[];

    setStopsFields('loadItems', loadItems);
    setSmallestLoadItemId(props.smallestLoadItemId() - 1);
  };

  const copyLoadItem = (index: number) => {
    const loadItems = [...props.data().loadItems];
    const loadItem = loadItems[index];
    loadItems.push({
      ...loadItem,
      id: props.smallestLoadItemId(),
      operationType: 'Insert',
      ...(loadItem.loadItemBreakdowns
        ? {
            loadItemBreakdowns: cloneBreakdownsForInsert(
              loadItem.loadItemBreakdowns,
            ),
          }
        : {}),
    });
    setStopsFields('loadItems', loadItems);
    setSmallestLoadItemId(props.smallestLoadItemId() - 1);
  };

  // Reuse one empty record when the stop has no facility details yet.
  // A fresh object on every read gives the hours rows a new identity and
  // Solid drops the focused input.
  const emptyStopFacility = createStopFacilityDetails();
  const stopFacility = () => props.data().facilityDetails ?? emptyStopFacility;
  const showFacilityUi = () =>
    props.mode === 'TL' &&
    isFeatureFlagEnabled(FeatureFlags.AddressBookFacilityDetails)();

  const handleAddToAddressBook = async () => {
    const {
      locationName,
      address1,
      address2,
      city,
      state,
      zip,
      countryCode,
      instructions,
      directions,
      description,
      phone,
      email,
      contact,
      facilityDetails,
    } = props.data();
    if (!Boolean(orderStore.order.customerId)) {
      handleToast(
        ToastType.Caution,
        'Please select a customer for the order to add to address book.',
      );
      return;
    }
    setAddingToAddressBook(true);

    try {
      const success = await setToAddressBook({
        customerId: orderStore.order.customerId!,
        name: locationName,
        specialInstructions: instructions,
        addressLine1: address1,
        addressLine2: address2,
        city: city,
        state: state,
        zip: zip,
        openTimeFrom: null,
        openTimeTo: null,
        contactName: contact,
        contactPhone: phone,
        contactEmail: email,
        countryCode: countryCode,
        internalNote: description,
        drivingDirections: directions,
        facilityDetails:
          props.mode === 'TL' &&
          isFeatureFlagEnabled(FeatureFlags.AddressBookFacilityDetails)() &&
          facilityDetails
            ? stopFacilityToFacility(facilityDetails)
            : undefined,
      });

      if (success) {
        handleToast(
          ToastType.Success,
          'Address added to address book successfully',
        );
      } else {
        handleToast(ToastType.Error, 'Failed to add address to address book');
      }
    } catch (error) {
      logError('handleAddToAddressBook', error as Error);
      handleToast(ToastType.Error, (error as Error).message);
    } finally {
      setAddingToAddressBook(false);
    }
  };

  const setAddressFromStopList = (stop: LastUsedTopStopModel) => {
    setStopsFields('address1', stop.address1 ?? '');
    setStopsFields('address2', stop.address2 ?? '');
    setStopsFields('city', stop.city ?? '');
    setStopsFields('state', stop.state ?? '');
    setStopsFields('zip', stop.zip);
    setStopsFields('contact', stop.contact ?? '');
    setStopsFields('locationName', stop.locationName ?? '');
    setStopsFields('phone', stop.phone ?? '');
    setStopsFields('email', stop.email ?? '');
    setStopsFields('instructions', stop.instructions ?? '');
    setStopsFields('directions', stop.directions ?? '');
    setStopsFields('countryCode', stop.countryCode ?? 'USA');

    getLocationTimezone(stop.countryCode ?? 'US', stop.zip)
      .then((timezone) => {
        if (timezone !== undefined) {
          // const abbr = getTimeZoneAbbreviation(timezone);
          setFields('timezone', timezone);
        } else {
          setFields('timezone', 'CST');
        }
      })
      .catch((e) => {
        printError(e);
      });
  };

  const handleRadioClick = (type: string) => {
    let loadItems = cloneDeep(props.data().loadItems);
    if (type === 'Pick Up') {
      setStopsFields('pickUp', true);
      setStopsFields('dropOff', false);
      setStopsFields('destination', false);
    } else {
      setStopsFields('pickUp', false);
      setStopsFields('dropOff', true);
      setStopsFields('origin', false);
    }
    loadItems = loadItems.map((itm) => {
      if (itm.operationType === 'Delete') return itm;
      if (type === 'Pick Up' && itm.dropOffId === props.data().id) {
        itm.dropOffId = null;
        itm.pickUpId = props.data().id;
        itm.operationType =
          itm.operationType !== 'Insert' ? 'Update' : itm.operationType;
      } else if (type === 'Drop Off' && itm.pickUpId === props.data().id) {
        itm.pickUpId = null;
        itm.dropOffId = props.data().id;
        itm.operationType =
          itm.operationType !== 'Insert' ? 'Update' : itm.operationType;
      }
      return itm;
    });
    setStopsFields('loadItems', loadItems);

    //check Edi Vals as we use same fields for both pickup and dropoff
    setDriverOutAuto214ManualOverride();
    setDriverInAuto214ManualOverride();
    setApptointmentAuto214ManualOverride();
  };

  const meetsEdiUpdatesTrackingRequirement = (): boolean => {
    return Boolean(props.stop && props.stop.id > 0 && props.mode !== 'LTL');
  };
  const checkCustomerFeatureFlags = (feature: string) => {
    return get(
      featureFlagGetCustomerFeatures,
      `featureFlagCustomer.${feature}`,
      false,
    );
  };

  const areDatesEqual = (firstDateStr: string, secondDateStr: string) => {
    const firstDate = DateTime.fromISO(firstDateStr, { zone: 'utc' });

    const secondDate = DateTime.fromISO(secondDateStr, { zone: 'utc' });

    return firstDate.toMillis() === secondDate.toMillis();
  };

  const setDriverOutAuto214ManualOverride = () => {
    if (meetsEdiUpdatesTrackingRequirement()) {
      const hasAgentSetDriverOutTime = !areDatesEqual(
        props.initialStopVals.driverDateOut,
        convertDateTimeV2(
          props.data().driverDateOut,
          props.data().driverTimeOut,
        ),
      );
      if (
        checkCustomerFeatureFlags('EDIPickupDriverOutAuto214') &&
        props.data().pickUp
      ) {
        setFields(
          'shouldSendEdiBooleanVals.shouldSendPickupDriverOutAuto214',
          hasAgentSetDriverOutTime,
        );
      } else if (
        checkCustomerFeatureFlags('EDIDropoffDriverOutAuto214') &&
        props.data().dropOff
      ) {
        setFields(
          'shouldSendEdiBooleanVals.shouldSendDropoffDriverOutAuto214',
          hasAgentSetDriverOutTime,
        );
      }
    }
  };

  const setDriverInAuto214ManualOverride = () => {
    if (meetsEdiUpdatesTrackingRequirement()) {
      const hasAgentSetDriverOutTime = !areDatesEqual(
        props.initialStopVals.driverDateIn,
        convertDateTimeV2(props.data().driverDateIn, props.data().driverTimeIn),
      );
      if (
        checkCustomerFeatureFlags('EDIPickupDriverInAuto214') &&
        props.data().pickUp
      ) {
        setFields(
          'shouldSendEdiBooleanVals.shouldSendPickupDriverInAuto214',
          hasAgentSetDriverOutTime,
        );
      } else if (
        checkCustomerFeatureFlags('EDIDropoffDriverInAuto214') &&
        props.data().dropOff
      ) {
        setFields(
          'shouldSendEdiBooleanVals.shouldSendDropoffDriverInAuto214',
          hasAgentSetDriverOutTime,
        );
      }
    }
  };

  const activeAppointmentDateTime = () => {
    const stop = props.data();
    if (
      isFacilityAppointmentType() &&
      isFeatureFlagEnabled(FeatureFlags.MilitaryTime)()
    ) {
      const time = timeIntegerToTimeString(stop.beginTimeRange);
      return time
        ? convertDateTimeV2(stop.stopDateTime, time)
        : stop.stopDateTime
          ? convertDateTimeV2(stop.stopDateTime, stop.stopDateTime)
          : '';
    }
    return convertDateTimeV2(stop.dateAppointment, stop.timeAppointment);
  };

  const setApptointmentAuto214ManualOverride = () => {
    if (meetsEdiUpdatesTrackingRequirement()) {
      const hasAgentSetAppointmentTime =
        !areDatesEqual(
          props.initialStopVals.appointmentDate,
          activeAppointmentDateTime(),
        ) ||
        props.data().appointmentNum !== props.initialStopVals.appointmentNum;
      if (
        checkCustomerFeatureFlags('EDIPickupAppointmentAuto214') &&
        props.data().pickUp
      ) {
        setFields(
          'shouldSendEdiBooleanVals.shouldSendPickupAppointmentAuto214',
          hasAgentSetAppointmentTime,
        );
      } else if (
        checkCustomerFeatureFlags('EDIDropoffAppointmentAuto214') &&
        props.data().dropOff
      ) {
        setFields(
          'shouldSendEdiBooleanVals.shouldSendDropoffAppointmentAuto214',
          hasAgentSetAppointmentTime,
        );
      }
    }
  };

  return (
    <form ref={props.form}>
      <Grid container>
        <Grid item size={12}>
          <Grid container spacing={6}>
            <Grid item size={{ xs: 12, md: 3 }}>
              <StopListContainer
                setAddressFromStopList={setAddressFromStopList}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 4.5 }}>
              <Grid
                container
                spacing={2}
                rowSpacing={2}
                columnSpacing={2}
                class="gap-x-2"
              >
                <Grid item size={12}>
                  <div class="mobile:text-xs 1080p:text-lg font-semibold bg-[#f1f1f1]	rounded-md p-1">
                    Location
                  </div>
                </Grid>
                <Grid item size={12}>
                  <Show when={showFacilityUi()}>
                    <StopAddressBookSync
                      data={props.data}
                      setField={setStopsFields}
                      customerId={() => orderStore.order.customerId}
                      stopCanSave={props.stopCanSave}
                    />
                  </Show>
                  <div class="flex justify-end">
                    <div
                      style={
                        orderStore.order.customerId === null
                          ? {
                              color: 'rgba(0, 0, 0, 0.26)',
                            }
                          : undefined
                      }
                      class={`text-xs font-bold ${
                        orderStore.order.customerId === null
                          ? ' Mui-disabled'
                          : 'cursor-pointer  hover:underline'
                      } mb-2`}
                      onClick={handleSelectFromAddressBook}
                    >
                      Select from Address Book
                    </div>
                  </div>
                  {/**
                   * This is the original autocomplete. I've replaced it with one below. If there's any problem,
                   * just comment that one out and re-enable this one.
                   */}
                  {/* <AutoComplete
                    id="autocomplete-stop-modal-name"
                    endpoint="stop/autocomplete?expression"
                    defaultValue=""
                    label="Stop Name"
                    value={props.data().locationName}
                    onChange={(itm: string) =>
                      setStopsFields('locationName', itm)
                    }
                    onItemSelect={(item) => {
                      setAddressFromStopList(item as LastUsedTopStopModel);
                    }}
                    renderItem={(item) => (
                      <div>
                        {(item as LastUsedTopStopModel).locationName}
                        <div>
                          <div>{(item as LastUsedTopStopModel).address1}</div>
                          <div>
                            {(item as LastUsedTopStopModel).city},{' '}
                            {(item as LastUsedTopStopModel).state}{' '}
                            {(item as LastUsedTopStopModel).zip}
                          </div>
                        </div>
                      </div>
                    )}
                    error={props.errors().locationName}
                  /> */}

                  <DebounceAutoComplete
                    id="autocomplete-stop-modal-name"
                    endpoint="stop/autocomplete?expression="
                    defaultValue=""
                    label="Stop Name"
                    inputSanitizer={removeEmojis}
                    useV3Client={false}
                    value={props.data().locationName}
                    valueName="locationName"
                    debounceDelay={1000}
                    onChange={(itm: string) =>
                      setStopsFields('locationName', itm)
                    }
                    onItemSelect={(item) => {
                      setAddressFromStopList(item as LastUsedTopStopModel);
                    }}
                    renderItem={(item) => (
                      <div>
                        {(item as LastUsedTopStopModel).locationName}
                        <div>
                          <div>{(item as LastUsedTopStopModel).address1}</div>
                          <div>
                            {(item as LastUsedTopStopModel).city},{' '}
                            {(item as LastUsedTopStopModel).state}{' '}
                            {(item as LastUsedTopStopModel).zip}
                          </div>
                        </div>
                      </div>
                    )}
                    error={props.errors().locationName}
                  />
                </Grid>
                <Grid item size={{ xs: 12, md: 6, lg: 12 }}>
                  <IconLabel
                    label="Address"
                    content={
                      <RadioGroup
                        value={props.data().pickUp ? 'Pick Up' : 'Drop Off'}
                        onChange={handleRadioClick}
                        options={[
                          { label: 'Pick Up', value: 'Pick Up' },
                          { label: 'Drop off', value: 'Drop Off' },
                        ]}
                      />
                    }
                    textColorCondition={true}
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 12 }}>
                  <AddressAutoComplete
                    label="Address line 1"
                    required
                    placeholder="Enter Address"
                    value={props.data().address1}
                    onChange={(itm) => {
                      setStopsFields('address1', itm);
                    }}
                    onItemSelect={(item) => {
                      handleGooglePlacesSelect(item);
                    }}
                    zIndex="9999"
                    error={props.errors().address1}
                    maxLength={100}
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 12 }}>
                  <TextField
                    label="Address line 2"
                    placeholder="Address line 2"
                    fullWidth={true}
                    value={props.data().address2}
                    onChange={(itm: string) => setStopsFields('address2', itm)}
                    maxLength={100}
                    dataTestId="address-line-2-textInput"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    label="City"
                    required
                    placeholder="City"
                    error={props.errors().city}
                    value={props.data().city}
                    onChange={(itm: string) => setStopsFields('city', itm)}
                    maxLength={50}
                    dataTestId="city-textInput"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    label="State"
                    required
                    placeholder="State"
                    error={props.errors().state}
                    value={props.data().state}
                    onChange={(itm: string) => {
                      const sanitized = sanitizeStateInput(itm);
                      if (sanitized === props.data().state) return;
                      setStopsFields('state', sanitized);
                    }}
                    maxLength={2}
                    dataTestId="state-textInput"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    label="Zip"
                    required
                    placeholder="Zip"
                    value={props.data().zip}
                    onChange={(itm: string) => {
                      setStopsFields('zip', itm);
                      if (itm.length > 3) {
                        debouncedCheckTimezone(props.data().countryCode, itm);
                      }
                    }}
                    error={props.errors().zip}
                    maxLength={7}
                    dataTestId="zip-textInput"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                  <SelectInputCustom
                    label="USA/CAN/MEX"
                    menuItems={[
                      { label: 'USA', value: 'USA' },
                      { label: 'CAN', value: 'CAN' },
                      { label: 'MEX', value: 'MEX' },
                    ]}
                    value={props.data().countryCode}
                    onChange={(val) => {
                      setStopsFields('countryCode', val);
                    }}
                    dataTestId={formatDataTestId('Stop country', 'selectInput')}
                    size="md"
                  />
                </Grid>
                <Grid item size={12}>
                  <Button
                    label="+ Add to address book"
                    size="sm"
                    onClick={() => handleAddToAddressBook()}
                    isLoading={addingtoAddressBook()}
                    dataTestId={formatDataTestId(
                      '+ Add to address book',
                      'button',
                    )}
                  />
                </Grid>
                <Grid item size={12}>
                  <Divider />
                </Grid>
                <Grid item size={12}>
                  <div class="mobile:text-xs 1080p:text-lg font-semibold bg-[#f1f1f1]	rounded-md p-1">
                    Contact Info
                  </div>
                </Grid>
                <Grid item size={12}>
                  <TextField
                    label={'Name'}
                    placeholder="Name"
                    fullWidth={true}
                    value={props.data().contact}
                    onChange={(itm: string) => setStopsFields('contact', itm)}
                    error={props.errors().contact}
                    maxLength={50}
                    dataTestId="name-textInput"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Email"
                    fullWidth={true}
                    placeholder="Email"
                    error={props.errors().email}
                    value={props.data().email}
                    onChange={(itm: string) => setStopsFields('email', itm)}
                    dataTestId="email-textInput"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <PhoneInput
                    label="Phone"
                    placeholder="Phone Number"
                    error={props.errors().phone}
                    value={props.data().phone}
                    onChange={(value) => setStopsFields('phone', value)}
                    dataTestId="phone-phoneInput"
                  />
                </Grid>
                <Show when={showFacilityUi()}>
                  <Grid item size={12}>
                    <div class="mb-2">
                      <div class="font-bold text-armstrong-primary">
                        Facility Details
                      </div>
                      <p class="mb-3 text-xs text-primary">
                        Operating hours are informational only.
                      </p>
                      <SchedulingMethodFields
                        layout="stacked"
                        appointmentLabel="Appointment Type"
                        variant="stop"
                        value={() => ({
                          appointmentType:
                            props.data().facilityAppointmentType ?? '',
                          schedulingMethod:
                            props.data().facilitySchedulingMethod ?? '',
                          schedulingMethodDetails:
                            stopFacility().schedulingMethodDetails,
                          schedulingEmailCc: stopFacility().schedulingEmailCc,
                          portalUrl: stopFacility().portalUrl,
                          portalCredentials: stopFacility().portalCredentials,
                        })}
                        onChange={(value) => {
                          setStopsFields(
                            'facilityAppointmentType',
                            value.appointmentType,
                          );
                          setStopsFields(
                            'facilitySchedulingMethod',
                            value.schedulingMethod,
                          );
                          setStopsFields('facilityDetails', {
                            ...stopFacility(),
                            appointmentType: value.appointmentType,
                            schedulingMethod: value.schedulingMethod,
                            schedulingMethodDetails:
                              value.schedulingMethodDetails,
                            schedulingEmailCc: value.schedulingEmailCc,
                            portalUrl: value.portalUrl,
                            portalCredentials: value.portalCredentials,
                          });
                        }}
                      />
                      <div class="mt-4">
                        <WeeklyOperatingHoursEditor
                          facilityDetails={stopFacility}
                          onChange={(facilityDetails) =>
                            setStopsFields('facilityDetails', facilityDetails)
                          }
                          dataTestIdPrefix="stop-weekly-hours"
                          hideTimeIcon
                          stacked
                        />
                      </div>
                    </div>
                  </Grid>
                </Show>
              </Grid>
            </Grid>
            <Grid item size={{ xs: 12, md: 4.5 }}>
              <div class="mobile:text-xs 1080p:text-lg font-semibold bg-[#f1f1f1]	rounded-md p-1 mb-4">
                Stop Information
              </div>
              <Grid container spacing={2}>
                <Show
                  when={
                    props.mode === 'TL' &&
                    isFeatureFlagEnabled(
                      FeatureFlags.AddressBookFacilityDetails,
                    )()
                  }
                >
                  <Grid item size={12}>
                    <Grid
                      container
                      spacing={2}
                      columnSpacing={2}
                      class="items-center gap-x-2"
                    >
                      <Grid item size={12}>
                        <SelectInputCustom
                          label="Appointment Type"
                          menuItems={appointmentTypeOptions}
                          value={props.data().facilityAppointmentType}
                          onChange={(val: string) => {
                            setStopsFields('facilityAppointmentType', val);
                            setStopsFields('facilityDetails', {
                              ...stopFacility(),
                              appointmentType: val,
                            });
                          }}
                          dataTestId={formatDataTestId(
                            'Facility appointment type',
                            'selectInput',
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Show>
                <Grid item size={{ lg: 12, xl: 6 }}>
                  <Grid container>
                    <Grid item size={12}>
                      <DatePicker
                        label="Date"
                        value={props.data().stopDateTime}
                        error={props.errors().stopDateTime}
                        handleChange={(value: string) => {
                          setStopsFields('stopDateTime', value);
                          // When ltlStopModalCleanup is enabled, auto-populate
                          // shipping hours Open/Close dates from stop date
                          if (hideLtlFields()) {
                            if (!props.data().driverDateIn) {
                              setStopsFields('driverDateIn', value);
                            }
                            if (!props.data().driverDateOut) {
                              setStopsFields('driverDateOut', value);
                            }
                          }
                        }}
                        class="[&>.MuiFormControl-root]:p-0!"
                        asRawDate
                        dataTestId="stopDate"
                      />
                    </Grid>
                    {/* Time is being deprecated, but will remain visibile for older orders that already have a time value */}
                    <Show
                      when={
                        (!isFeatureFlagEnabled(FeatureFlags.MilitaryTime)() &&
                          !(
                            props.mode === 'TL' &&
                            isFeatureFlagEnabled(
                              FeatureFlags.AddressBookFacilityDetails,
                            )()
                          )) ||
                        (props.data().time &&
                          !props.data().timezone &&
                          props.data().beginTimeRange == null &&
                          props.data().endTimeRange == null)
                      }
                    >
                      <Grid item size={12} class="mt-2">
                        <TextField
                          value={props.data().time}
                          placeholder="Time"
                          label="Time"
                          error={props.errors().appointmentNum}
                          onChange={(itm: string) =>
                            setStopsFields('time', itm)
                          }
                          dataTestId="stopTimeField"
                        />
                      </Grid>
                    </Show>
                    <Show
                      when={
                        (isFeatureFlagEnabled(FeatureFlags.MilitaryTime)() ||
                          (props.mode === 'TL' &&
                            isFeatureFlagEnabled(
                              FeatureFlags.AddressBookFacilityDetails,
                            )())) &&
                        !hideLtlFields()
                      }
                    >
                      <Grid item size={12}>
                        <IntegerTimeInput
                          label="Begin Time (optional)"
                          placeholder="Select Begin Time"
                          allowMidnight
                          onChange={(value) => {
                            setStopsFields('beginTimeRange', value);
                          }}
                          value={props.data().beginTimeRange}
                          error={props.errors().beginTimeRange?.[0]}
                          dataTestId="stopBeginTime"
                        />
                      </Grid>
                      <Grid item size={12}>
                        <IntegerTimeInput
                          label="End Time"
                          placeholder="Select End Time"
                          allowMidnight
                          onChange={(value) => {
                            setStopsFields('endTimeRange', value);
                          }}
                          value={props.data().endTimeRange}
                          dataTestId="stopEndTime"
                        />
                      </Grid>
                      <Show
                        when={
                          isFeatureFlagEnabled(
                            FeatureFlags.RequiredBeginTimeRange,
                          )() ||
                          (props.mode === 'TL' &&
                            isFeatureFlagEnabled(
                              FeatureFlags.AddressBookFacilityDetails,
                            )())
                        }
                      >
                        <Grid item size={12} class="mt-2">
                          <SelectInputCustom
                            label="Time Zone"
                            menuItems={naTimezoneOptions.map((z) => ({
                              label: z.label,
                              value: String(z.value),
                            }))}
                            placeholder="Select Time Zone"
                            value={props.data().timezone}
                            onChange={(val: string) => {
                              setTimezoneOverride(val);
                            }}
                            dataTestId={formatDataTestId(
                              'Begin and End Time Range Time Zone',
                              'selectInput',
                            )}
                          />
                          <Dialog
                            open={Boolean(timezoneOverride())}
                            onClose={() => setTimezoneOverride(undefined)}
                            id="timezone-select-warning-modal"
                          >
                            <DialogContent class="pt-[20px]">
                              The timezone is determined by the selected stop
                              location. Manually overriding it may result in
                              incorrect scheduling. Are you sure you want to
                              continue?
                            </DialogContent>
                            <DialogActions>
                              <Button
                                variant="contained"
                                label="Ok"
                                type="submit"
                                dataTestId={formatDataTestId('Ok', 'button')}
                                onClick={() => {
                                  setFields('timezone', timezoneOverride());
                                  setTimezoneOverride(undefined);
                                }}
                              />
                              <Button
                                class="float-right"
                                variant="text"
                                label="Cancel"
                                type="button"
                                onClick={() => setTimezoneOverride(undefined)}
                                dataTestId={formatDataTestId(
                                  'Cancel',
                                  'button',
                                )}
                              />
                            </DialogActions>
                          </Dialog>
                        </Grid>
                      </Show>
                      <Show when={!showFacilityUi()}>
                        <Grid item size={12} class="mt-2">
                          <TextField
                            value={props.data().timeComments ?? ''}
                            placeholder="Scheduling Comments"
                            label="Scheduling Comments"
                            onChange={(itm: string) =>
                              setStopsFields('timeComments', itm)
                            }
                            dataTestId="time-comments-textInput"
                          />
                        </Grid>
                      </Show>
                    </Show>
                    <Grid item size={12} class="mt-2">
                      <TextField
                        label={props.data().pickUp ? 'Pickup #' : 'Dropoff #'}
                        placeholder={props.data().pickUp ? 'Pickup' : 'Dropoff'}
                        error={props.errors().referenceNum}
                        value={props.data().referenceNum}
                        onChange={(itm: string) =>
                          setStopsFields('referenceNum', itm)
                        }
                        dataTestId={'pickup-dropoff-number-textInput'}
                      />
                    </Grid>
                    <Grid item size={12}>
                      {orderStore.order.edi && (
                        <Grid item size={12} class="mt-2">
                          <TextField
                            label="Stop Code (EDI)"
                            placeholder=""
                            error={props.errors().stopCode}
                            value={props.data().stopCode}
                            onChange={(itm: string) =>
                              setStopsFields('stopCode', itm)
                            }
                            dataTestId="stop-code-edi-textInput"
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
                <Show when={!hideLtlFields()}>
                  <Grid item size={{ lg: 12, xl: 6 }}>
                    <Grid container spacing={2}>
                      <Grid item size={12}>
                        <div class="mobile:text-xs 1080p:text-md font-semibold  bg-[#f1f1f1]	rounded-md mt-2 p-1">
                          {props.mode === 'LTL' &&
                          isFeatureFlagEnabled(
                            FeatureFlags.AddressBookFacilityDetails,
                          )()
                            ? 'LTL Appointment'
                            : 'Appointment'}
                        </div>
                        <Show
                          when={
                            props.stop &&
                            props.stop.id > 0 &&
                            (props.data().pickUp
                              ? checkCustomerFeatureFlags(
                                  'EDIPickupAppointmentAuto214',
                                )
                              : checkCustomerFeatureFlags(
                                  'EDIDropoffAppointmentAuto214',
                                ))
                          }
                        >
                          <Checkbox
                            onChange={() => {
                              const field = props.data().pickUp
                                ? 'shouldSendPickupAppointmentAuto214'
                                : 'shouldSendDropoffAppointmentAuto214';
                              setFields(
                                `shouldSendEdiBooleanVals.${field}`,
                                !props.data().shouldSendEdiBooleanVals[field],
                              );
                            }}
                            checked={
                              props.data().shouldSendEdiBooleanVals[
                                props.data().pickUp
                                  ? 'shouldSendPickupAppointmentAuto214'
                                  : 'shouldSendDropoffAppointmentAuto214'
                              ]
                            }
                            label="Send EDI Update"
                            size="xs"
                            color="info"
                            class="ml-[-9px]"
                            dataTestId="send-edi-update-checkboxInput"
                          />
                        </Show>
                      </Grid>
                      <Show when={!showFacilityUi()}>
                        <Grid item size={12}>
                          <div class="border-2 p-2 border-slate-500 rounded-md">
                            <div class="flex gap-x-3">
                              <Checkbox
                                size="xs"
                                color="info"
                                class="ml-[-4px]"
                                checked={props.data().needsAppointment}
                                onChange={(val) => {
                                  setStopsFields('needsAppointment', val);
                                  if (!val) {
                                    setStopsFields(
                                      'appointmentSchedulingStatus',
                                      '',
                                    );
                                    setStopsFields(
                                      'aiSchedulingEnabled',
                                      false,
                                    );
                                  } else if (
                                    !props.data().appointmentSchedulingStatus
                                  ) {
                                    setStopsFields(
                                      'appointmentSchedulingStatus',
                                      'Required',
                                    );
                                  }
                                  setApptointmentAuto214ManualOverride();
                                }}
                                dataTestId="needs-appointment-checkboxInput"
                              />
                              <div class="flex flex-col gap-y-2">
                                <Show
                                  when={
                                    isFacilityAppointmentType() &&
                                    isFeatureFlagEnabled(
                                      FeatureFlags.MilitaryTime,
                                    )()
                                  }
                                  fallback={
                                    <>
                                      <DatePicker
                                        label="Date"
                                        value={props.data().dateAppointment}
                                        handleChange={(value: string) => {
                                          setStopsFields(
                                            'dateAppointment',
                                            value,
                                          );
                                          setApptointmentAuto214ManualOverride();
                                        }}
                                        class="[&>.MuiFormControl-root]:p-0!"
                                        dataTestId="appointmentDate"
                                      />
                                      <TimePicker
                                        label="Time"
                                        value={props.data().timeAppointment}
                                        onChange={(newTime) => {
                                          setStopsFields(
                                            'timeAppointment',
                                            newTime.includes('T:00')
                                              ? ''
                                              : newTime,
                                          );
                                          setApptointmentAuto214ManualOverride();
                                        }}
                                        error={props.errors().timeAppointment}
                                        dataTestId="appointmentTime"
                                      />
                                    </>
                                  }
                                >
                                  {/* Appointment type honors the begin date/time so military format stays in sync with the Stop Information section */}
                                  <DatePicker
                                    label="Date"
                                    value={props.data().stopDateTime}
                                    error={props.errors().stopDateTime}
                                    handleChange={(value: string) => {
                                      setStopsFields('stopDateTime', value);
                                      setApptointmentAuto214ManualOverride();
                                    }}
                                    class="[&>.MuiFormControl-root]:p-0!"
                                    asRawDate
                                    dataTestId="appointmentDate"
                                  />
                                  <IntegerTimeInput
                                    label="Time (optional)"
                                    placeholder="Select Time"
                                    allowMidnight
                                    onChange={(value) => {
                                      setStopsFields('beginTimeRange', value);
                                      setApptointmentAuto214ManualOverride();
                                    }}
                                    value={props.data().beginTimeRange}
                                    error={props.errors().beginTimeRange?.[0]}
                                    dataTestId="appointmentTime"
                                  />
                                </Show>
                                <Show
                                  when={
                                    props.mode !== 'LTL' &&
                                    ((props.stop &&
                                      props.stop.id > 0 &&
                                      props.data().pickUp &&
                                      checkCustomerFeatureFlags(
                                        'EDIPickupAppointmentAuto214',
                                      )) ||
                                      (props.data().dropOff &&
                                        checkCustomerFeatureFlags(
                                          'EDIDropoffAppointmentAuto214',
                                        )))
                                  }
                                >
                                  <SelectInputCustom
                                    label="Time Zone"
                                    menuItems={basicUSTimezoneOptions.map(
                                      (z) => ({
                                        label: z.label,
                                        value: String(z.value),
                                      }),
                                    )}
                                    placeholder="Select Time Zone"
                                    value={props.data().appointmentTimeZone}
                                    onChange={(val: string) => {
                                      setFields('appointmentTimeZone', val);
                                    }}
                                    dataTestId={formatDataTestId(
                                      'Appointment time zone',
                                      'selectInput',
                                    )}
                                  />
                                </Show>
                              </div>
                            </div>
                          </div>
                        </Grid>
                      </Show>
                      <Grid item size={12}>
                        <div class="mt-2">
                          <TextField
                            label="Appointment #"
                            placeholder="Appointment #"
                            error={props.errors().appointmentNum}
                            value={props.data().appointmentNum}
                            onChange={(itm: string) => {
                              setApptointmentAuto214ManualOverride();
                              setStopsFields('appointmentNum', itm);
                            }}
                            dataTestId="appointmentNumber"
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                </Show>
                <Show when={showFacilityUi()}>
                  <Grid item size={12}>
                    <button
                      type="button"
                      class="flex items-center gap-1 text-armstrong-primary"
                      aria-expanded={schedulingDetailsOpen()}
                      onClick={() =>
                        setSchedulingDetailsOpen(!schedulingDetailsOpen())
                      }
                      data-testid="scheduling-details-toggle"
                    >
                      <Show
                        when={schedulingDetailsOpen()}
                        fallback={<ChevronRightIcon size={18} />}
                      >
                        <ChevronDownIcon size={18} />
                      </Show>
                      <span class="text-base font-bold">
                        Scheduling Details
                      </span>
                    </button>
                    <Show when={schedulingDetailsOpen()}>
                      <Show
                        when={isFacilityAppointmentType()}
                        fallback={
                          <div class="mt-2">
                            <TextField
                              value={props.data().timeComments ?? ''}
                              placeholder="Scheduling comments"
                              label="Scheduling Comments"
                              fullWidth
                              multiline
                              rows={2}
                              onChange={(itm: string) =>
                                setStopsFields('timeComments', itm)
                              }
                              dataTestId="time-comments-textInput"
                            />
                          </div>
                        }
                      >
                        <div class="mt-2 flex flex-col gap-2">
                          <div class="flex items-center gap-1">
                            <Show
                              when={
                                props.data().appointmentSchedulingStatus ===
                                'ReadyToSchedule'
                              }
                              fallback={
                                <Tooltip
                                  title="Scheduling Status must be set to Ready to Schedule"
                                  dataTestId="ai-schedule-status-tooltip"
                                >
                                  <span class="inline-flex cursor-help items-center opacity-60">
                                    <Checkbox
                                      label="AI Schedule"
                                      checked={props.data().aiSchedulingEnabled}
                                      disabled
                                      onChange={(enabled: boolean) => {
                                        setStopsFields(
                                          'aiSchedulingEnabled',
                                          enabled,
                                        );
                                      }}
                                      dataTestId="ai-schedule-checkbox"
                                    />
                                  </span>
                                </Tooltip>
                              }
                            >
                              <Checkbox
                                label="AI Schedule"
                                checked={props.data().aiSchedulingEnabled}
                                onChange={(enabled: boolean) => {
                                  setStopsFields(
                                    'aiSchedulingEnabled',
                                    enabled,
                                  );
                                  const status =
                                    props.data().appointmentSchedulingStatus;
                                  if (
                                    !enabled ||
                                    (status && status !== 'Required')
                                  ) {
                                    return;
                                  }
                                  setStopsFields(
                                    'appointmentSchedulingStatus',
                                    'ReadyToSchedule',
                                  );
                                  setStopsFields('needsAppointment', true);
                                }}
                                dataTestId="ai-schedule-checkbox"
                              />
                            </Show>
                            <Tooltip
                              title="AI scheduling will attempt to set appointments as close to the Preferred Time as possible."
                              dataTestId="ai-schedule-info-tooltip"
                            >
                              <span class="inline-flex cursor-help items-center text-primary">
                                <InformationIcon size={16} />
                              </span>
                            </Tooltip>
                          </div>
                          <div class="grid grid-cols-2 items-start gap-2">
                            <SelectInputCustom
                              label="Scheduling Status"
                              menuItems={appointmentSchedulingStatusOptions}
                              value={props.data().appointmentSchedulingStatus}
                              placeholder="Select status"
                              error={
                                props.errors().appointmentSchedulingStatus?.[0]
                              }
                              onChange={(status: string) => {
                                setStopsFields(
                                  'appointmentSchedulingStatus',
                                  status,
                                );
                                if (status)
                                  setStopsFields('needsAppointment', true);
                                if (status !== 'ReadyToSchedule') {
                                  setStopsFields('aiSchedulingEnabled', false);
                                }
                              }}
                              dataTestId="scheduling-status-select"
                            />
                            <TextField
                              label="Preferred Time"
                              placeholder="Preferred Time"
                              class="h-[36px]! [&_input]:h-[36px]!"
                              value={props.data().appointmentPreference}
                              error={props.errors().appointmentPreference?.[0]}
                              onChange={(preference: string) =>
                                setStopsFields(
                                  'appointmentPreference',
                                  preference,
                                )
                              }
                              maxLength={500}
                              dataTestId="preferred-appointment-time-input"
                            />
                          </div>
                          <div
                            class="relative mt-2 rounded border border-input-border px-3 pb-3 pt-4"
                            data-testid="requested-appointment-container"
                          >
                            <span class="absolute -top-2 left-2 bg-white px-1 text-xs leading-4 text-primary">
                              Requested Appointment
                            </span>
                            <Checkbox
                              size="xs"
                              color="info"
                              checked={props.data().needsAppointment}
                              onChange={(val) => {
                                setStopsFields('needsAppointment', val);
                                if (!val) {
                                  setStopsFields(
                                    'appointmentSchedulingStatus',
                                    '',
                                  );
                                  setStopsFields('aiSchedulingEnabled', false);
                                } else if (
                                  !props.data().appointmentSchedulingStatus
                                ) {
                                  setStopsFields(
                                    'appointmentSchedulingStatus',
                                    'Required',
                                  );
                                }
                                setApptointmentAuto214ManualOverride();
                              }}
                              dataTestId="needs-appointment-checkboxInput"
                            />
                            <div class="mt-2 grid grid-cols-2 items-start gap-2">
                              <Show
                                when={
                                  isFacilityAppointmentType() &&
                                  isFeatureFlagEnabled(
                                    FeatureFlags.MilitaryTime,
                                  )()
                                }
                                fallback={
                                  <>
                                    <DatePicker
                                      label="Date"
                                      value={props.data().dateAppointment}
                                      handleChange={(value: string) => {
                                        setStopsFields(
                                          'dateAppointment',
                                          value,
                                        );
                                        setApptointmentAuto214ManualOverride();
                                      }}
                                      class="[&>input]:h-[36px]! [&>input]:py-0!"
                                      dataTestId="requested-date-textInput"
                                    />
                                    <TimePicker
                                      label="Time"
                                      class="h-[36px]! [&_input]:h-[36px]!"
                                      value={props.data().timeAppointment}
                                      onChange={(newTime) => {
                                        setStopsFields(
                                          'timeAppointment',
                                          newTime.includes('T:00')
                                            ? ''
                                            : newTime,
                                        );
                                        setApptointmentAuto214ManualOverride();
                                      }}
                                      error={props.errors().timeAppointment}
                                      dataTestId="requested-time-input"
                                    />
                                  </>
                                }
                              >
                                <DatePicker
                                  label="Date"
                                  value={props.data().stopDateTime}
                                  error={props.errors().stopDateTime}
                                  handleChange={(value: string) => {
                                    setStopsFields('stopDateTime', value);
                                    setApptointmentAuto214ManualOverride();
                                  }}
                                  class="[&>input]:h-[36px]! [&>input]:py-0!"
                                  asRawDate
                                  dataTestId="requested-date-textInput"
                                />
                                <IntegerTimeInput
                                  label="Time"
                                  placeholder="Select Time"
                                  allowMidnight
                                  floatLabel
                                  variant="form"
                                  height="h-full"
                                  class="w-full [&>*:nth-child(2)]:box-border [&>*:nth-child(2)]:h-[36px]! [&>*:nth-child(2)]:max-h-[36px]! [&>*:nth-child(2)]:overflow-hidden [&>*:nth-child(2)]:p-0! [&_input]:h-full! [&_input]:min-h-0! [&_input]:py-0!"
                                  onChange={(value) => {
                                    setStopsFields('beginTimeRange', value);
                                    setApptointmentAuto214ManualOverride();
                                  }}
                                  value={props.data().beginTimeRange}
                                  error={props.errors().beginTimeRange?.[0]}
                                  dataTestId="requested-time-input"
                                />
                              </Show>
                            </div>
                            <Show
                              when={
                                props.mode !== 'LTL' &&
                                ((props.stop &&
                                  props.stop.id > 0 &&
                                  props.data().pickUp &&
                                  checkCustomerFeatureFlags(
                                    'EDIPickupAppointmentAuto214',
                                  )) ||
                                  (props.data().dropOff &&
                                    checkCustomerFeatureFlags(
                                      'EDIDropoffAppointmentAuto214',
                                    )))
                              }
                            >
                              <div class="mt-2">
                                <SelectInputCustom
                                  label="Time Zone"
                                  menuItems={basicUSTimezoneOptions.map(
                                    (z) => ({
                                      label: z.label,
                                      value: String(z.value),
                                    }),
                                  )}
                                  placeholder="Select Time Zone"
                                  value={props.data().appointmentTimeZone}
                                  onChange={(val: string) => {
                                    setFields('appointmentTimeZone', val);
                                  }}
                                  dataTestId={formatDataTestId(
                                    'Appointment time zone',
                                    'selectInput',
                                  )}
                                />
                              </div>
                            </Show>
                          </div>
                          <TextField
                            value={props.data().timeComments ?? ''}
                            placeholder="Scheduling comments"
                            label="Scheduling Comments"
                            fullWidth
                            multiline
                            rows={2}
                            onChange={(itm: string) =>
                              setStopsFields('timeComments', itm)
                            }
                            dataTestId="scheduling-comments-textInput"
                          />
                        </div>
                      </Show>
                    </Show>
                  </Grid>
                </Show>
                <Show
                  when={isFeatureFlagEnabled(
                    FeatureFlags.feature_show_drop_trailer_accessorial,
                  )()}
                >
                  <Checkbox
                    label="Drop Trailer"
                    checked={props.data().dropTrailer}
                    onChange={(val) => setStopsFields('dropTrailer', val)}
                  />
                </Show>
                <Show when={!isFacilityAppointmentType()}>
                  <Grid item size={12}>
                    <Grid container spacing={2}>
                      <Grid item size={{ xs: 12, sm: 6 }}>
                        <div class="mobile:text-xs 1080p:text-md font-semibold  bg-[#f1f1f1]	rounded-md p-1 mb-2 flex items-center">
                          {orderStore.order.loads[orderStore.activeTab.index]
                            .mode === 'LTL'
                            ? 'Shipping Hours (Open)*'
                            : 'Driver In Time'}

                          <Show when={props.mode !== 'LTL'}>
                            <Show
                              when={
                                props.stop &&
                                props.stop.id > 0 &&
                                (props.data().pickUp
                                  ? checkCustomerFeatureFlags(
                                      'EDIPickupDriverInAuto214',
                                    )
                                  : checkCustomerFeatureFlags(
                                      'EDIDropoffDriverInAuto214',
                                    ))
                              }
                            >
                              <Checkbox
                                onChange={() => {
                                  const field = props.data().pickUp
                                    ? 'shouldSendPickupDriverInAuto214'
                                    : 'shouldSendDropoffDriverInAuto214';
                                  setFields(
                                    `shouldSendEdiBooleanVals.${field}`,
                                    !props.data().shouldSendEdiBooleanVals[
                                      field
                                    ],
                                  );
                                }}
                                checked={
                                  props.data().shouldSendEdiBooleanVals[
                                    props.data().pickUp
                                      ? 'shouldSendPickupDriverInAuto214'
                                      : 'shouldSendDropoffDriverInAuto214'
                                  ]
                                }
                                dataTestId="shouldSendEdiBooleanVals"
                                label="Send EDI Update"
                                size="xs"
                                color="info"
                                class="font-normal! ml-1"
                              />
                            </Show>
                          </Show>
                        </div>
                        <DatePicker
                          value={props.data().driverDateIn}
                          handleChange={(value: string) => {
                            if (
                              !validateDriverTimeChange(
                                'driverInTime',
                                value,
                                props.data().driverTimeIn,
                                props.data().driverDateOut,
                                props.data().driverTimeOut,
                              )
                            ) {
                              return;
                            }
                            setStopsFields('driverDateIn', value);
                            setDriverInAuto214ManualOverride();
                          }}
                          class="[&>.MuiFormControl-root]:p-0!"
                          error={props.errors().driverDateIn}
                          dataTestId={'driverDateIn'}
                          disabled={
                            !canEditDriverInTimeForEditor(
                              props.stop?.id,
                              props.mode,
                            )
                          }
                        />
                        <div class="mt-2">
                          <Show
                            when={is24HourTimesEnabled()}
                            fallback={
                              <TimePicker
                                value={props.data().driverTimeIn}
                                onChange={(newTime) => {
                                  if (
                                    !validateDriverTimeChange(
                                      'driverInTime',
                                      props.data().driverDateIn,
                                      newTime,
                                      props.data().driverDateOut,
                                      props.data().driverTimeOut,
                                    )
                                  ) {
                                    return;
                                  }
                                  setStopsFields('driverTimeIn', newTime);
                                  setDriverInAuto214ManualOverride();
                                }}
                                error={props.errors().driverTimeIn}
                                dataTestId={'driverTimeIn'}
                                disabled={
                                  !canEditDriverInTimeForEditor(
                                    props.stop?.id,
                                    props.mode,
                                  )
                                }
                              />
                            }
                          >
                            <IntegerTimeInput
                              placeholder="HH:MM"
                              allowManualEntry
                              allowMidnight
                              floatLabel
                              height="h-[30px]"
                              value={timeStringToTimeInteger(
                                driverTimeToHHmm(props.data().driverTimeIn),
                              )}
                              onChange={(value) => {
                                const newTime = timeIntegerToTimeString(value);
                                const isValid = validateDriverTimeChange(
                                  'driverInTime',
                                  props.data().driverDateIn,
                                  newTime,
                                  props.data().driverDateOut,
                                  props.data().driverTimeOut,
                                );
                                setStopsFields(
                                  'driverTimeIn',
                                  driverTimeToIso(
                                    newTime,
                                    props.data().driverTimeIn,
                                  ),
                                );
                                if (!isValid) return;
                                setDriverInAuto214ManualOverride();
                              }}
                              error={props.errors().driverTimeIn?.[0]}
                              dataTestId={'driverTimeIn'}
                              disabled={
                                !canEditDriverInTimeForEditor(
                                  props.stop?.id,
                                  props.mode,
                                )
                              }
                            />
                          </Show>
                        </div>
                        <Show when={driverInChronologyError()}>
                          <div
                            class="text-xs text-red-600 mt-1"
                            data-testid="driverIn-chronology-error"
                          >
                            {driverInChronologyError()}
                          </div>
                        </Show>
                        <Show
                          when={
                            props.mode !== 'LTL' &&
                            ((props.stop &&
                              props.stop.id > 0 &&
                              props.data().pickUp &&
                              checkCustomerFeatureFlags(
                                'EDIPickupDriverInAuto214',
                              )) ||
                              (props.data().dropOff &&
                                checkCustomerFeatureFlags(
                                  'EDIDropoffDriverInAuto214',
                                )))
                          }
                        >
                          <Box mt={2}>
                            <SelectInputCustom
                              label="Time Zone"
                              menuItems={basicUSTimezoneOptions.map((z) => ({
                                label: z.label,
                                value: String(z.value),
                              }))}
                              placeholder="Select Time Zone"
                              value={props.data().driverInTimeZone}
                              onChange={(val: string) => {
                                setFields('driverInTimeZone', val);
                              }}
                              dataTestId={formatDataTestId(
                                'Driver in time zone',
                                'selectInput',
                              )}
                            />
                          </Box>
                        </Show>
                      </Grid>
                      <Grid item size={{ xs: 12, sm: 6 }}>
                        <div class="mobile:text-xs font-semibold 1080p:text-md bg-[#f1f1f1]	rounded-md p-1 mb-2 flex items-center px-2">
                          {orderStore.order.loads[orderStore.activeTab.index]
                            .mode === 'LTL'
                            ? 'Shipping Hours (Closed)*'
                            : 'Driver Out Time'}
                          <Show when={props.mode !== 'LTL'}>
                            <Show
                              when={
                                props.stop &&
                                props.stop.id > 0 &&
                                (props.data().pickUp
                                  ? checkCustomerFeatureFlags(
                                      'EDIPickupDriverOutAuto214',
                                    )
                                  : checkCustomerFeatureFlags(
                                      'EDIDropoffDriverOutAuto214',
                                    ))
                              }
                            >
                              <Checkbox
                                onChange={() => {
                                  const field = props.data().pickUp
                                    ? 'shouldSendPickupDriverOutAuto214'
                                    : 'shouldSendDropoffDriverOutAuto214';
                                  props.setFields(
                                    `shouldSendEdiBooleanVals.${field}`,
                                    !props.data().shouldSendEdiBooleanVals[
                                      field
                                    ],
                                  );
                                }}
                                checked={
                                  props.data().shouldSendEdiBooleanVals[
                                    props.data().pickUp
                                      ? 'shouldSendPickupDriverOutAuto214'
                                      : 'shouldSendDropoffDriverOutAuto214'
                                  ]
                                }
                                dataTestId="send-edi-update-driver-out-checkboxInput"
                                label="Send EDI Update"
                                size="xs"
                                color="info"
                                class="font-normal! ml-1"
                              />
                            </Show>
                          </Show>
                        </div>
                        <DatePicker
                          value={props.data().driverDateOut}
                          handleChange={(value: string) => {
                            if (
                              !validateDriverTimeChange(
                                'driverOutTime',
                                value,
                                props.data().driverTimeOut,
                                props.data().driverDateIn,
                                props.data().driverTimeIn,
                              )
                            ) {
                              return;
                            }
                            setStopsFields('driverDateOut', value);
                            setDriverOutAuto214ManualOverride();
                          }}
                          class="[&>.MuiFormControl-root]:p-0!"
                          error={props.errors().driverDateOut}
                          dataTestId={'driverDateOut'}
                          disabled={
                            !canEditDriverOutTimeForEditor(
                              props.stop?.id,
                              props.data().driverDateIn,
                              props.data().driverTimeIn,
                              props.mode,
                            )
                          }
                        />
                        <div class="mt-2">
                          <Show
                            when={is24HourTimesEnabled()}
                            fallback={
                              <TimePicker
                                value={props.data().driverTimeOut}
                                onChange={(newTime) => {
                                  if (
                                    !validateDriverTimeChange(
                                      'driverOutTime',
                                      props.data().driverDateOut,
                                      newTime,
                                      props.data().driverDateIn,
                                      props.data().driverTimeIn,
                                    )
                                  ) {
                                    return;
                                  }
                                  setStopsFields('driverTimeOut', newTime);
                                  setDriverOutAuto214ManualOverride();
                                }}
                                error={props.errors().driverTimeOut}
                                dataTestId={'driverTimeOut'}
                                disabled={
                                  !canEditDriverOutTimeForEditor(
                                    props.stop?.id,
                                    props.data().driverDateIn,
                                    props.data().driverTimeIn,
                                    props.mode,
                                  )
                                }
                              />
                            }
                          >
                            <IntegerTimeInput
                              placeholder="HH:MM"
                              allowManualEntry
                              allowMidnight
                              floatLabel
                              height="h-[30px]"
                              value={timeStringToTimeInteger(
                                driverTimeToHHmm(props.data().driverTimeOut),
                              )}
                              onChange={(value) => {
                                const newTime = timeIntegerToTimeString(value);
                                const isValid = validateDriverTimeChange(
                                  'driverOutTime',
                                  props.data().driverDateOut,
                                  newTime,
                                  props.data().driverDateIn,
                                  props.data().driverTimeIn,
                                );
                                setStopsFields(
                                  'driverTimeOut',
                                  driverTimeToIso(
                                    newTime,
                                    props.data().driverTimeOut,
                                  ),
                                );
                                if (!isValid) return;
                                setDriverOutAuto214ManualOverride();
                              }}
                              error={props.errors().driverTimeOut?.[0]}
                              dataTestId={'driverTimeOut'}
                              disabled={
                                !canEditDriverOutTimeForEditor(
                                  props.stop?.id,
                                  props.data().driverDateIn,
                                  props.data().driverTimeIn,
                                  props.mode,
                                )
                              }
                            />
                          </Show>
                        </div>
                        <Show when={driverOutChronologyError()}>
                          <div
                            class="text-xs text-red-600 mt-1"
                            data-testid="driverOut-chronology-error"
                          >
                            {driverOutChronologyError()}
                          </div>
                        </Show>
                        <Show
                          when={
                            props.mode !== 'LTL' &&
                            ((props.stop &&
                              props.stop.id > 0 &&
                              props.data().pickUp &&
                              checkCustomerFeatureFlags(
                                'EDIPickupDriverOutAuto214',
                              )) ||
                              (props.data().dropOff &&
                                checkCustomerFeatureFlags(
                                  'EDIDropoffDriverOutAuto214',
                                )))
                          }
                        >
                          <Box mt={2}>
                            <SelectInputCustom
                              label="Time Zone"
                              menuItems={basicUSTimezoneOptions.map((z) => ({
                                label: z.label,
                                value: String(z.value),
                              }))}
                              placeholder="Select Time Zone"
                              value={props.data().driverOutTimeZone}
                              onChange={(val: string) => {
                                setFields('driverOutTimeZone', val);
                              }}
                              dataTestId={formatDataTestId(
                                'Driver out time zone',
                                'selectInput',
                              )}
                            />
                          </Box>
                        </Show>
                      </Grid>
                    </Grid>
                  </Grid>
                </Show>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item size={12}>
          <Grid container>
            <Grid item>
              <div class="flex font-semibold text-lg bg-[#f1f1f1] mt-2	rounded-md p-1 items-center">
                <div>
                  Items {props.data().pickUp ? 'Picking Up' : 'Dropping Off'}
                </div>
                {props.mode === 'LTL' &&
                (props.hasDropOffStop || props.data().dropOff) ? (
                  <Button
                    disabled={!Boolean(orderStore.order.customerId)}
                    label="Open Product Catalog"
                    dataTestId={formatDataTestId(
                      'Open Product Catalog',
                      'button',
                    )}
                    onClick={() => setActiveView('ltl-product-catalog')}
                    startIcon={<PlusIcon size={20} />}
                    class="ml-[16px]!"
                    size="sm"
                  />
                ) : null}
              </div>
            </Grid>
            <Grid item size={12} class="mb-3">
              {!props.hasDropOffStop && !props.data().dropOff ? (
                <Notification
                  tableRowNotification={false}
                  type="info"
                  text="You will manage your inventory after creating a Drop Off stop."
                />
              ) : null}
              <div
                class={
                  !(!props.hasDropOffStop && !props.data().dropOff)
                    ? 'block'
                    : 'hidden'
                }
              >
                <StopItems
                  mode={props.mode}
                  loadItems={props.data().loadItems}
                  arrayName="loadItems"
                  setFields={setStopsFields}
                  addNewListItem={addNewListItem}
                  copyLoadItem={copyLoadItem}
                  stopType={props.data().pickUp ? 'Pick Up' : 'Drop Off'}
                  options={
                    props.data().pickUp
                      ? props.dropOffOptions
                      : props.pickUpOptions
                  }
                  stopIds={props.stopIds}
                  stopId={props.data().id}
                  //@ts-expect-error is ok
                  errors={props.errors().loadItems}
                />
                {
                  <span class="mt-2 inline-block">
                    <b>Weight: {getTotalWeight()} pounds </b>
                  </span>
                }
              </div>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
              <div class="flex justify-end items-center gap-x-1">
                <div class="text-xs	font-medium">Rate Con:</div>
                <MinusCircleIcon size={'14px'} color="#B00020" />
              </div>
              <TextField
                multiline
                rows={5}
                label="Internal Notes"
                placeholder="This field does not appear on the rate confirmation"
                value={props.data().description}
                onChange={(itm: string) => setStopsFields('description', itm)}
                error={props.errors().description}
                controlStyle={{ 'background-color': '#f7eaea' }}
                dataTestId="internal-notes-textAreaField"
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
              <div class="flex justify-end items-center gap-x-1">
                <div class="text-xs	font-medium">Rate Con:</div>
                <CheckIcon size={'14px'} color="#248670" />
              </div>
              <TextField
                multiline
                rows={5}
                label={'Directions'}
                placeholder="This field will appear on the rate confirmation"
                value={props.data().directions}
                onChange={(itm: string) => setStopsFields('directions', itm)}
                error={props.errors().directions}
                controlStyle={{ 'background-color': '#e6efe6' }}
                dataTestId="directions-textAreaField"
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 12, md: 4 }}>
              <div class="flex justify-end items-center gap-x-1">
                <div class="text-xs	font-medium">Rate Con:</div>
                <CheckIcon size={'14px'} color="#248670" />
              </div>
              <TextField
                multiline
                rows={5}
                label={'Instructions'}
                placeholder="This field will appear on the rate confirmation"
                value={props.data().instructions}
                onChange={(itm: string) => setStopsFields('instructions', itm)}
                error={props.errors().instructions}
                controlStyle={{ 'background-color': '#e6efe6' }}
                dataTestId="instructions-textAreaField"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item size={12} class="flex justify-end items-center mt-2 gap-2">
          <Show when={props.requoteRequired}>
            <Notification
              type="warning"
              text="The address from the customer address book does not match what was quoted (city, state, or zip differs). You must requote before saving."
            />
          </Show>
          <span class="text-xs text-[#026EA1] font-roboto">
            Clicking 'Done' does not save edits made in the stop details to the
            load. Order save is still required.
          </span>
          <Button
            class="max-w-[150px]"
            label="Done"
            dataTestId={formatDataTestId('done', 'button')}
            type="submit"
            disabled={props.requoteRequired}
            onClick={() => {
              props.handleSubmit();
            }}
          />
          <Button
            class="max-w-[150px]"
            variant=""
            dataTestId={formatDataTestId('cancel', 'button')}
            label="Cancel"
            onClick={() => {
              closeModal(props.modalId);
            }}
          />
        </Grid>
      </Grid>
    </form>
  );
};
